import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updateEmployeeGoal } from '../../../shared/api/employee.service';

type UseGoalIntroFormParams = {
  initialGoalText?: string;
  initialTargetAmount?: number;
};

const EMPLOYEE_GOAL_QUERY_KEY = ['employee', 'goal'] as const;
const GOAL_TEXT_MIN_LENGTH = 1;
const GOAL_TEXT_MAX_LENGTH = 200;

function normalizeGoalText(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.slice(0, GOAL_TEXT_MAX_LENGTH);
}

export function useGoalIntroForm({
  initialGoalText: providedGoalText,
  initialTargetAmount: providedTargetAmount,
}: UseGoalIntroFormParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const goalSave = useMutation({
    mutationFn: updateEmployeeGoal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_GOAL_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const initialGoalText = providedGoalText ?? '';
  const initialTargetAmount = providedTargetAmount ?? 0;
  const [goalText, setGoalTextState] = useState(() => initialGoalText);
  const canSubmit = goalText.trim().length >= GOAL_TEXT_MIN_LENGTH;

  const setGoalText = (nextValue: string) => {
    if (goalSave.isError) {
      goalSave.reset();
    }

    setGoalTextState(normalizeGoalText(nextValue));
  };

  const persistGoal = async (nextGoalText: string) => {
    if (goalSave.isPending) {
      return false;
    }

    try {
      await goalSave.mutateAsync({
        targetAmount: initialTargetAmount,
        text: nextGoalText,
      });

      return true;
    } catch {
      return false;
    }
  };

  return {
    canSubmit,
    goalText,
    isSubmitting: goalSave.isPending,
    skip: () => persistGoal(''),
    setGoalText,
    submit: () => persistGoal(goalText.trim()),
    submitError: goalSave.isError ? t('login.networkError') : null,
  };
}
